"use client"

import { useState } from "react"
import type { IntakeFormSubmissionSummary } from "@/schema"
import { Button, Popover, PopoverContent, PopoverTrigger, ScrollArea } from "@ui/shad"
import { formatDistanceToNow } from "date-fns"
import { ClipboardList } from "lucide-react"

import { isAllBooksId } from "@/lib/all-books"
import { useFetchUnreadIntakeFormSubmissions } from "@/hooks/api/intake-forms"
import { useBookId } from "@/hooks/use-book-id"
import { useLoadingRouter } from "@/hooks/use-loading-router"

export function IntakeFormNeedsReviewPopover() {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const bookId = useBookId()
  const { push } = useLoadingRouter()

  const enabled = Boolean(bookId) && !isAllBooksId(bookId)
  const { data } = useFetchUnreadIntakeFormSubmissions(enabled)

  const submissions = data?.intake_form_submissions ?? []
  const count = data?.pagination?.total_count ?? 0

  if (count === 0) return null

  const goToSubmission = (submission: IntakeFormSubmissionSummary) => {
    setIsPopoverOpen(false)
    push(`/intake-forms/submissions/${submission.id}`)
  }

  const goToAllSubmissions = () => {
    setIsPopoverOpen(false)
    push("/intake-forms?tab=submissions")
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="iconSm" className="relative">
          <ClipboardList className="size-4" />
          <span className="bg-red-accent text-background absolute top-0 right-0 flex size-4 items-center justify-center rounded-full text-[10px]">
            {count}
          </span>
          <span className="sr-only">Form submissions to review</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" alignOffset={-20} className="w-96 p-0">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Form submissions to review</h3>
        </div>
        <ScrollArea className="max-h-80">
          <div className="divide-y">
            {submissions.map((submission) => (
              <button
                key={submission.id}
                type="button"
                className="hover:bg-muted flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
                onClick={() => goToSubmission(submission)}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{submission.respondent_name}</p>
                  <p className="text-muted-foreground text-xs">
                    {submission.intake_form_version.name} &middot;{" "}
                    {submission.submitted_at
                      ? formatDistanceToNow(new Date(submission.submitted_at), { addSuffix: true })
                      : "In progress"}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
        {count > submissions.length && (
          <button
            type="button"
            className="hover:bg-muted text-muted-foreground w-full border-t px-4 py-2 text-center text-xs transition-colors"
            onClick={goToAllSubmissions}
          >
            View all {count} submissions
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
